<script setup lang="ts">
/**
 * Investor wallets directory container.
 *
 * Composes:
 *   - list query (server-side pagination + filters)
 *   - filter drawer with investor name / email / KYC status / min /
 *     max balance / currency
 *
 * Directory view — no detail page yet, no row click, no mutations.
 * Operator copies investor id via a small CopyButton on each row.
 */
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Message from '@uiKit/elements/Message.vue'

import { useFilterUrlSync } from '@shared/composables/useFilterUrlSync'
import { formatToInternational } from '@shared/utils'
import { routes } from '@app/router/routes'

import WalletsDirectoryFilterDrawer, {
  type WalletsDirectoryFilterValues,
} from '../components/WalletsDirectoryFilterDrawer.vue'
import WalletsDirectoryList from '../components/WalletsDirectoryList.vue'

import { useWalletsDirectoryListQuery } from '../api/queries/useWalletsDirectoryListQuery'
import { KYC_STATUS_OPTIONS } from '../utils/walletsDirectoryFormatters'

import type { GetWalletsDirectoryParams, WalletDirectoryRow } from '../types/walletsDirectoryTypes'

// ─── Filters + pagination (server-side) ─────────────────────────────────────

const currentPage = ref(1)
const pageSize = ref(50)

const filterValues = ref<WalletsDirectoryFilterValues>({
  investorName: '',
  investorEmail: '',
  kycStatus: null,
  minBalance: '',
  maxBalance: '',
  currencyCode: 'AED',
})

const filterDrawerVisible = ref(false)

const KYC_STATUS_VALUES = KYC_STATUS_OPTIONS.map((o) => o.value)

useFilterUrlSync(filterValues, {
  toQuery: (state) => ({
    investorName: state.investorName || undefined,
    investorEmail: state.investorEmail || undefined,
    kycStatus: state.kycStatus ?? undefined,
    minBalance: state.minBalance || undefined,
    maxBalance: state.maxBalance || undefined,
    // currencyCode defaults to AED — only persist when changed away
    // from the default so a refresh of the default view stays clean.
    currencyCode:
      state.currencyCode && state.currencyCode !== 'AED' ? state.currencyCode : undefined,
  }),
  fromQuery: (query) => {
    const seed: Partial<WalletsDirectoryFilterValues> = {}
    if (typeof query.investorName === 'string') seed.investorName = query.investorName
    if (typeof query.investorEmail === 'string') seed.investorEmail = query.investorEmail
    if (typeof query.kycStatus === 'string' && KYC_STATUS_VALUES.includes(query.kycStatus)) {
      seed.kycStatus = query.kycStatus
    }
    if (typeof query.minBalance === 'string') seed.minBalance = query.minBalance
    if (typeof query.maxBalance === 'string') seed.maxBalance = query.maxBalance
    if (typeof query.currencyCode === 'string') seed.currencyCode = query.currencyCode
    return seed
  },
  onFiltersChange: () => {
    currentPage.value = 1
  },
})

const listParams = computed<GetWalletsDirectoryParams>(() => {
  const params: GetWalletsDirectoryParams = {
    page: currentPage.value,
    limit: pageSize.value,
  }
  if (filterValues.value.investorName) params.investorName = filterValues.value.investorName
  if (filterValues.value.investorEmail) params.investorEmail = filterValues.value.investorEmail
  if (filterValues.value.kycStatus) params.kycStatus = filterValues.value.kycStatus
  if (filterValues.value.minBalance) params.minBalance = filterValues.value.minBalance
  if (filterValues.value.maxBalance) params.maxBalance = filterValues.value.maxBalance
  if (filterValues.value.currencyCode) params.currencyCode = filterValues.value.currencyCode
  return params
})

const {
  data: listData,
  isLoading: listLoading,
  isFetching: listFetching,
  error: listError,
  refetch: refetchList,
} = useWalletsDirectoryListQuery(listParams)

const totalWallets = computed(() => listData.value?.meta.total ?? 0)
const headerCountLabel = computed(() => {
  if (listLoading.value) return '…'
  return `${formatToInternational(totalWallets.value)} wallets`
})

function onFiltersApply(next: WalletsDirectoryFilterValues) {
  filterValues.value = { ...next }
  currentPage.value = 1
}

function onFiltersReset() {
  filterValues.value = {
    investorName: '',
    investorEmail: '',
    kycStatus: null,
    minBalance: '',
    maxBalance: '',
    currencyCode: 'AED',
  }
  currentPage.value = 1
}

const activeFilterCount = computed(() => {
  const f = filterValues.value
  let count = 0
  if (f.investorName) count += 1
  if (f.investorEmail) count += 1
  if (f.kycStatus) count += 1
  if (f.minBalance) count += 1
  if (f.maxBalance) count += 1
  // currencyCode defaults to AED and is always set; only count when changed away from default
  if (f.currencyCode && f.currencyCode !== 'AED') count += 1
  return count
})

const hasActiveFilters = computed(() => activeFilterCount.value > 0)

const emptyTitle = computed(() =>
  hasActiveFilters.value ? 'No wallets match your filters' : 'No investor wallets',
)
const emptySubtitle = computed(() =>
  hasActiveFilters.value
    ? 'Clear filters to see all wallets.'
    : 'Investor wallets will appear here as accounts are created.',
)

function onSearchChange(value: string) {
  filterValues.value = { ...filterValues.value, investorName: value }
  currentPage.value = 1
}

function openFilterDrawer() {
  filterDrawerVisible.value = true
}

// ─── View transactions deep link ────────────────────────────────────────────
//
// Navigate to the fiat-transactions list pre-filtered to this investor's
// transactions. The list container reads `route.query.investorId` on mount
// (alongside `investorBankAccountId` and `treasuryAccountId`).
const router = useRouter()

function handleViewTransactions(row: WalletDirectoryRow) {
  router.push({
    path: routes.TREASURY_FIAT_TRANSACTIONS_LIST,
    query: { investorId: row.investorId },
  })
}

function describeError(err: unknown): string {
  if (!err) return ''
  if (err instanceof Error) return err.message
  const candidate = err as { message?: unknown; code?: unknown }
  if (typeof candidate.message === 'string') return candidate.message
  if (typeof candidate.code === 'string') return candidate.code
  return 'Something went wrong. Please try again.'
}
</script>

<template>
  <Box flex direction="col" gap="4">
    <Message v-if="listError" color="danger" variant="outlined" size="small">
      <Box flex justify="between" align="center" gap="3">
        <Text type="body-sm">Could not load investor wallets. {{ describeError(listError) }}</Text>
        <Button label="Try again" variant="text" color="danger" @click="refetchList()" />
      </Box>
    </Message>

    <WalletsDirectoryList
      title="Investor wallets"
      :subtitle="headerCountLabel"
      :rows="listData?.data ?? []"
      :loading="listLoading || listFetching"
      :total="totalWallets"
      :page="currentPage"
      :page-size="pageSize"
      :filter-count="activeFilterCount"
      :search="filterValues.investorName"
      :empty-title="emptyTitle"
      :empty-subtitle="emptySubtitle"
      @update:page="currentPage = $event"
      @update:page-size="pageSize = $event"
      @update:search="onSearchChange"
      @filter-click="openFilterDrawer"
      @view-transactions="handleViewTransactions"
    />
  </Box>

  <WalletsDirectoryFilterDrawer
    :visible="filterDrawerVisible"
    :values="filterValues"
    @update:visible="filterDrawerVisible = $event"
    @apply="onFiltersApply"
    @reset="onFiltersReset"
  />
</template>
